import React, { useRef, useState } from "react";
import {
  AccountVerificationStatus,
  VerificationStepDetail,
} from "../../interfaces/verification.interface";

interface VerificationStepsPanelProps {
  title?: string;
  verification: AccountVerificationStatus | null;
  renderStepExtra?: (step: VerificationStepDetail) => React.ReactNode;
}

const formatFieldValue = (value: string | number | boolean) => {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  return String(value);
};

const VerificationStepsPanel: React.FC<VerificationStepsPanelProps> = ({
  title = "Account Verification",
  verification,
  renderStepExtra,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  if (!verification) {
    return (
      <div className="rounded-xl border border-gray-600 bg-gray-800/60 p-4 text-gray-400 text-sm">
        Verification data unavailable.
      </div>
    );
  }

  const { stepCount, completedCount, isAccountVerified, stepDetails } =
    verification;
  const percentage = stepCount > 0 ? (completedCount / stepCount) * 100 : 0;
  const radius = 40;
  const stroke = 6;
  const normalizedRadius = radius - stroke;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const handleStepClick = (index: number) => {
    setActiveIndex(index);
    cardRefs.current[index]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  };

  const renderFieldRow = (
    field: NonNullable<VerificationStepDetail["fields"]>[number],
    fieldIndex: number
  ) => (
    <div
      key={`${field.label}-${fieldIndex}`}
      className="flex justify-between gap-4 py-1.5 border-b border-gray-700/50 last:border-0 text-sm"
    >
      <span className="text-gray-400 shrink-0">{field.label}</span>
      <span
        className={`text-right break-all ${
          field.ok === true
            ? "text-green-400"
            : field.ok === false
              ? "text-red-400"
              : "text-gray-200"
        }`}
      >
        {formatFieldValue(field.value)}
      </span>
    </div>
  );

  return (
    <div className="verification-steps-panel space-y-4">
      <h2 className="text-xl font-semibold text-yellow-500 text-left">{title}</h2>

      <div className="rounded-xl border border-gray-600 bg-gray-800/60 p-4 flex flex-col sm:flex-row items-center gap-6">
        <div className="relative shrink-0">
          <svg width={radius * 2} height={radius * 2} className="-rotate-90">
            <circle
              cx={radius}
              cy={radius}
              r={normalizedRadius}
              fill="transparent"
              stroke="#374151"
              strokeWidth={stroke}
            />
            <circle
              cx={radius}
              cy={radius}
              r={normalizedRadius}
              fill="transparent"
              stroke="#eab308"
              strokeWidth={stroke}
              strokeDasharray={`${circumference} ${circumference}`}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            <span className="text-lg font-bold text-yellow-400">
              {completedCount}/{stepCount}
            </span>
            <span className="text-[10px] text-gray-400">steps</span>
          </div>
        </div>

        <div className="flex-1 text-center sm:text-left">
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
              isAccountVerified
                ? "bg-green-500/20 text-green-300 border border-green-400/40"
                : "bg-amber-500/20 text-amber-300 border border-amber-400/40"
            }`}
          >
            {isAccountVerified ? "Fully Verified" : "In Progress"}
          </span>
          <p className="text-gray-300 text-sm mt-2">
            {completedCount} of {stepCount} verification steps completed (
            {Math.round(percentage)}%)
          </p>
        </div>
      </div>

      <div className="flex overflow-x-auto py-2 gap-2 no-scrollbar border-b border-gray-700">
        {stepDetails.map((step, index) => {
          const isActive = index === activeIndex;
          const isCompleted = step.completed;
          return (
            <button
              key={step.key}
              type="button"
              onClick={() => handleStepClick(index)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                isActive
                  ? "bg-yellow-500 text-gray-900 shadow-lg scale-105"
                  : isCompleted
                    ? "bg-green-500/20 text-green-300 border border-green-400/40"
                    : "bg-gray-700/60 text-gray-400 border border-gray-600 hover:bg-gray-700"
              }`}
            >
              {isCompleted && (
                <span className="text-[10px] flex items-center justify-center bg-green-500 text-white rounded-full w-3.5 h-3.5 font-bold">
                  ✓
                </span>
              )}
              <span>
                {index + 1}. {step.label}
              </span>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {stepDetails.map((step, index) => (
          <div
            key={step.key}
            ref={(el) => {
              cardRefs.current[index] = el;
            }}
            className={`rounded-xl border p-4 transition-all ${
              index === activeIndex
                ? "border-yellow-500/60 bg-gray-800/80 ring-1 ring-yellow-500/30"
                : "border-gray-600 bg-gray-800/60"
            }`}
          >
            <div className="flex items-start justify-between gap-2 mb-3">
              <h3 className="text-yellow-400 font-semibold text-left text-sm">
                {index + 1}. {step.label}
              </h3>
              <span
                className={`shrink-0 inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                  step.completed
                    ? "bg-green-500/20 text-green-300"
                    : "bg-red-500/20 text-red-300"
                }`}
              >
                {step.completed ? "Completed" : "Pending"}
              </span>
            </div>
            {step.fields?.length ? (
              <div className="space-y-0">
                {step.fields.map(renderFieldRow)}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No field data available.</p>
            )}
            {renderStepExtra?.(step)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default VerificationStepsPanel;
